<script setup lang="ts">
import { computed } from 'vue'
import { PlusIcon } from '@heroicons/vue/24/outline'
import type { IAreaSensor, IManagementArea } from '@/interfaces/entities/management'
import { useManagementStore } from '@/store/management'
import AreaColumnItem from './AreaColumnItem.vue'

const props = defineProps<{
  parent: IManagementArea | null
  columnIndex: number
}>()

const emit = defineEmits<{
  (e: 'add-area', parentId: number | null): void
  (e: 'add-sensor', area: IManagementArea): void
  (e: 'edit-area', area: IManagementArea): void
  (e: 'remove-area', area: IManagementArea): void
  (e: 'edit-sensor', payload: { area: IManagementArea; areaSensor: IAreaSensor }): void
  (e: 'remove-sensor', payload: { area: IManagementArea; areaSensor: IAreaSensor }): void
}>()

const store = useManagementStore()

const title = computed(() => props.parent?.name ?? 'Empresa')
const areas = computed<IManagementArea[]>(() =>
  props.parent ? props.parent.children || [] : store.roots,
)
const sensors = computed<IAreaSensor[]>(() => props.parent?.management_area_sensors || [])
const parentId = computed(() => props.parent?.id ?? null)

const isAreaActive = (areaId: number) => store.selectedPath[props.columnIndex] === areaId
const isSensorActive = (sensorId: number) =>
  store.selectedSensorId === sensorId && store.selectedPath.length === props.columnIndex
</script>

<template>
  <div class="flex max-h-[360px] w-72 shrink-0 flex-col border-r border-stroke bg-white">
    <div class="flex shrink-0 items-center justify-between gap-2 border-b border-stroke bg-slate-50 px-3 py-2">
      <h4 class="min-w-0 flex-1 truncate text-sm font-semibold text-slate-700">{{ title }}</h4>
      <div class="flex shrink-0 items-center gap-1">
        <button
          type="button"
          class="flex items-center gap-0.5 px-1.5 py-1 text-xs text-primary hover:bg-primary/10"
          title="Adicionar área"
          @click="emit('add-area', parentId)">
          <PlusIcon class="h-3.5 w-3.5" /> área
        </button>
        <button
          v-if="parent"
          type="button"
          class="flex items-center gap-0.5 px-1.5 py-1 text-xs text-primary hover:bg-primary/10"
          title="Adicionar sensor"
          @click="emit('add-sensor', parent)">
          <PlusIcon class="h-3.5 w-3.5" /> sensor
        </button>
      </div>
    </div>

    <div class="flex-1 overflow-y-auto">
      <AreaColumnItem
        v-for="area in areas"
        :key="`a-${area.id}`"
        kind="area"
        :label="area.name"
        :active="isAreaActive(area.id)"
        :favorite="store.favoriteAreaId === area.id"
        :has-children="(area.children?.length || 0) > 0 || (area.management_area_sensors?.length || 0) > 0"
        @select="store.selectArea(columnIndex, area.id)"
        @edit="emit('edit-area', area)"
        @remove="emit('remove-area', area)"
        @toggle-favorite="store.setFavoriteArea(store.favoriteAreaId === area.id ? null : area.id)" />

      <AreaColumnItem
        v-for="areaSensor in sensors"
        :key="`s-${areaSensor.id}`"
        kind="sensor"
        :label="areaSensor.sensor_name"
        :sensor-type="areaSensor.sensor_type"
        :active="isSensorActive(areaSensor.sensor_id)"
        @select="store.selectSensor(columnIndex, areaSensor.sensor_id)"
        @edit="parent && emit('edit-sensor', { area: parent, areaSensor })"
        @remove="parent && emit('remove-sensor', { area: parent, areaSensor })" />

      <p
        v-if="!areas.length && !sensors.length"
        class="px-3 py-6 text-center text-xs text-slate-400">
        Vazio. Adicione áreas ou sensores.
      </p>
    </div>
  </div>
</template>
